import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isToday, startOfMonth, startOfWeek } from 'date-fns'

import { campaignStatus, CAMPAIGN_STATUS_LABELS } from '@iws/theme-schema/campaign'

import { Button } from '@/components/ui/button'
import { stringifyGraph } from '@/clients/http'
import { OfferModel as OfferApi } from '@/http/models/Offer'
import { cn } from '@/lib/utils'
import { OfferModel } from '@/types'

import { CAMPAIGN_STATUS_STYLES } from './status-styles'

interface Props {
  onEdit: (campaign: OfferModel.Offer) => void
  appliesTo?: OfferModel.AppliesTo
}

const PAGE = 100
const MAX_PAGES = 10

/** Every campaign (the API has no date filter): up to MAX_PAGES pages of PAGE, deleted ones left out. */
const useAllCampaigns = (appliesTo?: OfferModel.AppliesTo) => {
  const [data, setData] = useState<OfferModel.Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [truncated, setTruncated] = useState(false)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const all: OfferModel.Offer[] = []
      for (let page = 1; page <= MAX_PAGES; page++) {
        const res = await OfferApi.getList({ query: { page, length: PAGE, applies_to: appliesTo, graph_enc: stringifyGraph({ '*': true }) } })
        const batch = res.success ? res.data?.result ?? [] : []
        all.push(...batch)
        if (batch.length < PAGE || all.length >= (res.data?.count ?? 0)) break
        if (page === MAX_PAGES) setTruncated(true)
      }
      if (!cancelled) {
        setData(all.filter((c) => !c.deleted_at))
        setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [appliesTo])
  return { data, loading, truncated }
}

/** Month view of all campaigns: each shows on every day it runs. */
export const CampaignCalendar = ({ onEdit, appliesTo }: Props) => {
  const { data, loading, truncated } = useAllCampaigns(appliesTo)
  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const days = useMemo(
    () => eachDayOfInterval({ start: startOfWeek(month), end: endOfWeek(endOfMonth(month)) }),
    [month],
  )
  const onDay = (day: Date) => {
    const start = day.getTime()
    const end = start + 86_400_000
    return data.filter((c) => Date.parse(c.starts_at) < end && Date.parse(c.ends_at) >= start)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="icon" aria-label="Previous month" onClick={() => setMonth((m) => addMonths(m, -1))}>
          <ChevronLeft />
        </Button>
        <h2 className="text-heading-md text-ink" aria-live="polite">{format(month, 'MMMM yyyy')}</h2>
        <Button variant="outline" size="icon" aria-label="Next month" onClick={() => setMonth((m) => addMonths(m, 1))}>
          <ChevronRight />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-border bg-border-subtle text-xs">
        {days.slice(0, 7).map((d) => (
          <div key={d.toISOString()} className="bg-subdued px-2 py-1.5 font-medium text-text-2">{format(d, 'EEE')}</div>
        ))}
        {days.map((d) => (
          <div key={d.toISOString()} className={cn('min-h-24 space-y-1 bg-card p-1.5', !isSameMonth(d, month) && 'opacity-50')}>
            <div className={cn('text-right', isToday(d) && 'font-semibold text-ink')}>{format(d, 'd')}</div>
            {onDay(d).slice(0, 3).map((c) => {
              const status = campaignStatus(c)
              return (
                <button
                  key={c._id}
                  type="button"
                  onClick={() => onEdit(c)}
                  title={`${c.name} — ${CAMPAIGN_STATUS_LABELS[status]}`}
                  className={cn('block w-full truncate rounded-sm px-1.5 py-0.5 text-left font-medium', CAMPAIGN_STATUS_STYLES[status])}
                >
                  {c.name}
                </button>
              )
            })}
            {onDay(d).length > 3 && <div className="px-1 text-muted-foreground">+{onDay(d).length - 3} more</div>}
          </div>
        ))}
      </div>
      {loading && <p className="text-xs text-muted-foreground">Loading campaigns…</p>}
      {truncated && <p className="text-xs text-muted-foreground">Showing the first {PAGE * MAX_PAGES} campaigns.</p>}
    </div>
  )
}
